import { NotSetUp, RequireAuth, useApiClient, useAuth, useConfig, useSigninCopy } from "@procurement-agents/auth";
import { AdminApp, AgentsApp, ScopeAgentApp, TemplatesApp } from "@procurement-agents/scope-agent-ui";
import { scopeApi, type TenantInfo } from "@procurement-agents/scope-api";
import { useEffect, useMemo, useState } from "react";
import { Link, Navigate, Route, Routes } from "react-router-dom";
import { Landing } from "./Landing";
import { ModuleMenu } from "./ModuleMenu";

export function App() {
  const config = useConfig();
  const copy = useSigninCopy(config.api_base);   // the customer's name, copy and colour: public, and applied on the sign-in page too
  return (
    <RequireAuth>
      <Workspace publicName={copy ? copy.display_name || "Workspace" : null} />
    </RequireAuth>
  );
}

/** `publicName`: the customer's name from the public sign-in copy, already loaded before sign-in; null while it loads.
 *  The header uses it until the signed-in tenant call answers, so it never reads "Workspace" first (6 Oct). */
function Workspace({ publicName }: { publicName: string | null }) {
  const auth = useAuth();
  const config = useConfig();
  const fetcher = useApiClient();
  const api = useMemo(() => scopeApi(fetcher), [fetcher]);
  const [tenant, setTenant] = useState<TenantInfo | null>(null);
  const hasScope = auth.user?.agents.includes("scope");
  const smeOnly = !!auth.user && hasScope && auth.user.roles.length > 0 && auth.user.roles.every((r) => r === "sme");
  useEffect(() => { if (hasScope) api.tenant().then(setTenant).catch(() => setTenant(null)); }, [api, hasScope]);
  // signed in, but the account carries no agent: the sign-in page's "not set up yet" state, not an empty workspace
  if (!auth.user?.agents.length) return <NotSetUp />;
  return (
    <div className="workspace">
      <header className="topbar">
        <Link to="/" className="brand">{tenant?.display_name ?? publicName ?? ""}</Link>
        <div className="row">
          <ModuleMenu />
          <span className="muted small">{auth.user?.email ?? auth.user?.sub}</span>
          <button className="btn quiet" onClick={auth.logout}>Sign out</button>
        </div>
      </header>
      <Routes>
        <Route path="/" element={smeOnly ? <Navigate to="/scope" replace /> : <Landing api={api} />} />
        <Route path="/scope/*" element={<ScopeAgentApp currency={tenant?.currency} opening={tenant?.opening} domains={tenant?.sme_domains ?? []} voice={tenant?.voice} />} />
        <Route path="/templates/*" element={<TemplatesApp />} />
        <Route path="/agents/*" element={<AgentsApp />} />
        <Route path="/admin/*" element={<AdminApp />} />
        <Route path="/reports" element={<Reports url={config.report_agent_url} />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </div>
  );
}

/** Hands over to the report agent. `replace`, not `assign`: /reports is not kept in the history, so Back from the
 *  report agent returns to the workspace instead of landing here and being sent straight back. */
export function Reports({ url, go = (to: string) => window.location.replace(to) }: { url?: string; go?: (to: string) => void }) {
  useEffect(() => { if (url) go(url); }, [url]); // eslint-disable-line react-hooks/exhaustive-deps
  return <p className="muted center">{url ? "Taking you to the report agent…" : "The report agent is not configured for this workspace."}</p>;
}
